import { AxisDataAxisPathPoint } from '@cmps/ng-chart';
import { TranslateService } from '@ngx-translate/core';
import { endOfDay } from 'date-fns';

import { sdpNumber, sdpPercent } from '../../common/pipe/single-decimal-point';
import { AxisDataAxisPath } from '../../scorecard-mi2/chart/axis-data-axis-path';
import { TrendDialogMode } from '../../scorecard-mi2/trend-dialog';
import { TrendAxisDataAxisPathPointInsight } from '../../scorecard-mi2/trend-dialog/model/trend-axis-data-axis-path-point-insight.model ';
import { epicDependencies } from '../../store';
import { CoachingItemsOaScoreTypeTranslationMap } from '../constants/coaching-items-score-type-translation-map';
import { rect } from '../constants/trend-chart-rect-config';
import { CoachingTrendData } from '../models/coaching-trend-data.model';
import {
    MyCoachingCardGroup,
    TrendData,
} from '../models/my-coaching-card-group.model';
import { OaScoreType } from '../models/oa-score-type.enum';
import { TranslationKeyMap } from '../my-coaching-card.i18n';

import { createTargetPoints } from './create-target-points.function';
import { getLeftTicks } from './get-left-ticks.function';
import { getLegends } from './get-legends.function';

export function createPathPoints(
    trendData: TrendData[],
    color?: string,
    key?: string,
): AxisDataAxisPathPoint[] {
    return (
        trendData
            ?.filter((e) => e.score != null)
            .map((trendItem) => {
                const pd = (trendItem.periodDate as any as string)
                    ?.trim()
                    ?.toLowerCase();
                return {
                    x: endOfDay(
                        pd?.endsWith('z')
                            ? new Date(pd.slice(0, -1))
                            : new Date(pd),
                    ),
                    y: trendItem.score * 10,
                    actual: sdpNumber(trendItem.score * 100),
                    name: key,
                    color,
                    target:
                        trendItem.target != null
                            ? sdpNumber(trendItem.target * 100)
                            : '-',
                    pctToTarget: sdpPercent(trendItem.pctTgt),
                };
            }) ?? []
    );
}

function reorderPaths(
    paths: AxisDataAxisPath[],
    translateService,
): AxisDataAxisPath[] {
    const oaPathIndex = paths.findIndex(
        (path) =>
            path.key ===
            translateService.instant(
                CoachingItemsOaScoreTypeTranslationMap[OaScoreType.OA],
            ),
    );
    if (oaPathIndex !== -1 && oaPathIndex < paths.length - 1) {
        paths.push(paths.splice(oaPathIndex, 1)[0]);
    }
    return paths;
}

export function getOaTrendDataSet(
    oaTrendData: MyCoachingCardGroup[],
    selectedRowIds: OaScoreType[],
    isTypeFloa?: boolean,
): CoachingTrendData {
    const translateService =
        epicDependencies.injector.get<TranslateService>(TranslateService);
    // Filter Site OA and Reorder oaTrendData
    oaTrendData = oaTrendData
        ?.filter((e) => e.type !== OaScoreType.Site_OA)
        .sort((a, b) => (a.type === OaScoreType.OA ? -1 : 1));

    oaTrendData = oaTrendData?.map((e, index) => {
        return {
            ...e,
            color: ' #1e60d4',
        };
    });
    let paths: AxisDataAxisPath[] = [];

    oaTrendData.forEach((e) => {
        const isTypeOA = e.type === OaScoreType.OA;

        if (isTypeOA || selectedRowIds?.includes(e.type)) {
            const sortedData = e.trend.sort((a, b) => {
                return (
                    new Date(a.periodDate).getTime() -
                    new Date(b.periodDate).getTime()
                );
            });

            const key =
                isTypeFloa === true
                    ? translateService.instant(TranslationKeyMap._4005_FLOA__)
                    : translateService.instant(
                          CoachingItemsOaScoreTypeTranslationMap[e.type],
                      );
            const point: TrendAxisDataAxisPathPointInsight[] = createPathPoints(
                sortedData,
                e.color,
                key,
            );

            const targetPoints = createTargetPoints(sortedData, '#000');
            paths.push({
                key,
                points: point,
            });
            const targetKey = translateService.instant(
                TranslationKeyMap._726_TARGET,
            );
            paths.push({
                key: targetKey,
                points: targetPoints,
            });
        }
    });

    // Reorder  paths
    paths = reorderPaths(paths, translateService);

    const leftTicks = getLeftTicks();
    const legends = getLegends(paths);

    const trendTitleKey =
        isTypeFloa === true
            ? TranslationKeyMap._10077_FRONT_LINE_OA_TREND
            : TranslationKeyMap._10073_OPPORTUNITY_ALIGNMENT_TREND;
    const yAxisTitleKey =
        isTypeFloa === true
            ? TranslationKeyMap._10078_FLOA_Score
            : TranslationKeyMap._9854_OA_SCORE;

    const trendTitle = translateService.instant(trendTitleKey);
    const yAxisTitle = translateService.instant(yAxisTitleKey);

    const data: CoachingTrendData = {
        mode: TrendDialogMode.INSIGHT_TREND,
        showYAxisLabel2: false,
        leftTicks,
        rightTicks: [],
        paths,
        legends,
        rect,
        trendTitle,
        yAxisTitle,
    };

    return data;
}
